import {
  Icons,
  StatusChipV2,
  StatusChipV2Color,
  StatusChipV2Props,
  Tooltip,
  Typography,
  Stack,
  tokens,
  TooltipProps,
} from '@cast/design-system';

import { ReviewInstallationScriptButton } from '../components/ReviewInstallationScriptButton';
import { CUSTOM_EDGE_PROVIDER } from '../constants';
import { EdgeLocationMappedState, EnhancedEdgeLocation } from '../types';

const stateToColor: Record<EdgeLocationMappedState, StatusChipV2Color> = {
  [EdgeLocationMappedState.Pending]: 'neutral',
  [EdgeLocationMappedState.InUse]: 'success',
  [EdgeLocationMappedState.Offboarding]: 'neutral',
  [EdgeLocationMappedState.Failed]: 'error',
  [EdgeLocationMappedState.Deleted]: 'error',
  [EdgeLocationMappedState.Ready]: 'info',
  [EdgeLocationMappedState.Unknown]: 'neutral',
};

type EdgeLocationStateChipProps = Omit<StatusChipV2Props, 'label' | 'color'> & {
  state: EdgeLocationMappedState;
};

export const EdgeLocationStateChip = ({
  state,
  size = 'medium',
  variant = 'subtle',
  ...chipProps
}: EdgeLocationStateChipProps) => {
  return (
    <StatusChipV2
      label={state}
      color={stateToColor[state]}
      size={size}
      variant={variant}
      {...chipProps}
      testId="edge-location-state-chip"
    />
  );
};

EdgeLocationStateChip.displayName = 'EdgeLocationStateChip';

type EdgeLocationStateChipWithWarningProps = {
  edgeLocation: EnhancedEdgeLocation;
  clusterId: string;
  includeTooltip?: boolean;
};

const WarningIcon = (
  <Icons.Warning weight="fill" color={tokens.theme.fg.status.warning} />
);

const StyledTooltip = (props: TooltipProps) => {
  return (
    <Tooltip
      color="tertiary"
      sx={{ maxWidth: 254 }}
      arrow
      enterNextDelay={300}
      placement="bottom"
      {...props}
    />
  );
};

export const EdgeLocationStateChipWithWarning = ({
  edgeLocation,
  clusterId,
  includeTooltip = false,
}: EdgeLocationStateChipWithWarningProps) => {
  const { mappedState: state, templateUsage, provider, error } = edgeLocation;

  switch (state) {
    case EdgeLocationMappedState.Failed:
      return (
        <StyledTooltip
          title={
            <Stack gap={8} data-testid="edge-location-state-tooltip">
              <Typography variant="P12R">Configuration error</Typography>
              <Typography variant="P12R">
                {error ||
                  'Edge location resource setup is incomplete or invalid. Re-run the installation script to resolve the issue.'}
              </Typography>
              <ReviewInstallationScriptButton
                clusterId={clusterId}
                edgeLocationId={edgeLocation.id!}
                variant="text"
                hideSuccessNotification
                text="Rerun onboarding script"
              />
            </Stack>
          }
          wrapIf={includeTooltip && provider !== CUSTOM_EDGE_PROVIDER}
        >
          <EdgeLocationStateChip state={state} />
        </StyledTooltip>
      );
    case EdgeLocationMappedState.Pending:
      return (
        <StyledTooltip
          title={
            <Stack gap={8}>
              <Typography variant="P12R">Pending setup</Typography>
              <Typography variant="P12R">
                Complete the setup by running the installation script in your
                cloud shell or terminal.
              </Typography>
              <ReviewInstallationScriptButton
                clusterId={clusterId}
                edgeLocationId={edgeLocation.id!}
                variant="text"
                hideSuccessNotification
              />
            </Stack>
          }
          wrapIf={includeTooltip && provider !== CUSTOM_EDGE_PROVIDER}
        >
          <EdgeLocationStateChip
            loading={provider === CUSTOM_EDGE_PROVIDER}
            slots={
              provider !== CUSTOM_EDGE_PROVIDER
                ? { icon: WarningIcon }
                : undefined
            }
            state={state}
          />
        </StyledTooltip>
      );

    case EdgeLocationMappedState.InUse: {
      const hasDisabledTemplates =
        templateUsage.totalUsed > templateUsage.totalEnabled;

      return (
        <StyledTooltip
          title="Some node templates linked to this edge location are disabled."
          wrapIf={includeTooltip && hasDisabledTemplates}
        >
          <EdgeLocationStateChip
            slots={hasDisabledTemplates ? { icon: WarningIcon } : undefined}
            state={state}
          />
        </StyledTooltip>
      );
    }

    case EdgeLocationMappedState.Offboarding:
      return <EdgeLocationStateChip state={state} loading />;

    default:
      return <EdgeLocationStateChip state={state} />;
  }
};
